import { UploadOutlined, WarningOutlined } from '@ant-design/icons';
import { Alert, Button, List, message, Modal, Space, Upload } from 'antd';
import { RcFile, UploadFile, UploadProps } from 'antd/lib/upload/interface';
import { useState } from 'react';

import { useImportStoppagesMutation } from '../../../../services/stoppageApi';
import { Stopover, StoppageType } from '../../../../types';

type ErrorDetail = {
  item: string | number;
  error_type: string;
  message: string;
  overlap_with_item?: string | number;
};

type ImportResponse = {
  imported_count: number;
  stoppages?: StoppageType[];
  error_count?: number;
  error_details?: ErrorDetail[];
  [key: string]: unknown;
};

type StoppagesImportModalProps = {
  isOpen: boolean;
  onCancel: () => void;
  selectedStopover: Stopover;
  onSuccess: (data?: ImportResponse) => void;
};

export function StoppagesImportModal(props: StoppagesImportModalProps) {
  const { isOpen, onCancel, selectedStopover, onSuccess } = props;
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [validationErrors, setValidationErrors] = useState<ErrorDetail[]>([]);
  const [importStoppages, { isLoading }] = useImportStoppagesMutation();

  const handleBeforeUpload = (file: RcFile) => {
    const isXlsx =
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls');

    if (!isXlsx) {
      message.error(
        'Você só pode fazer upload de arquivos Excel (.xlsx ou .xls)'
      );
      return Upload.LIST_IGNORE;
    }

    setValidationErrors([]);
    setFileList([file]);
    return false;
  };

  const handleRemoveFile = () => {
    setFileList([]);
    return true;
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('Por favor, selecione um arquivo Excel para importar');
      return;
    }

    setValidationErrors([]);

    try {
      const operationId = selectedStopover?.operation?.id;
      if (!operationId) {
        message.error(
          'ID da operação não encontrado. Por favor, feche e abra o drawer novamente.'
        );
        return;
      }

      let file: File;
      const currentFile = fileList[0];

      if ('originFileObj' in currentFile && currentFile.originFileObj) {
        file = currentFile.originFileObj as File;
      } else if (currentFile instanceof File) {
        file = currentFile;
      } else {
        message.error('Tipo de arquivo inválido');
        return;
      }
      const response = await importStoppages({
        file,
        operation_id: operationId,
      }).unwrap();

      onSuccess(response);

      setFileList([]);
      setValidationErrors([]);
      onCancel();
    } catch (error: unknown) {
      const apiError = error as {
        status?: number;
        data?: {
          imported_count?: number;
          error_count?: number;
          error_details?: ErrorDetail[];
        };
      };

      if (
        apiError?.data?.error_details &&
        Array.isArray(apiError.data.error_details) &&
        apiError.data.error_details.length > 0
      ) {
        setValidationErrors(apiError.data.error_details);
        message.error(
          `Encontrados ${apiError.data.error_count} erro(s) na planilha. Nenhuma paralisação foi importada.`,
          5
        );
      } else {
        let errorMessage = 'Ocorreu um erro ao importar as paralisações.';

        if (apiError?.data) {
          errorMessage = `Erro na importação: ${JSON.stringify(apiError.data)}`;
        } else if (apiError?.status) {
          errorMessage = `Erro ${apiError.status}. Por favor, selecione novamente a planilha e tente novamente ou contate o suporte.`;
        } else {
          errorMessage = `Erro desconhecido: ${String(error)}`;
        }

        message.error(errorMessage, 10);
      }
    }
  };

  const handleCancel = () => {
    setValidationErrors([]);
    setFileList([]);
    onCancel();
  };

  const uploadProps: UploadProps = {
    beforeUpload: handleBeforeUpload,
    onRemove: handleRemoveFile,
    maxCount: 1,
    fileList,
    accept: '.xlsx,.xls',
  };

  const getErrorTypeLabel = (errorType: string): string => {
    const labels: Record<string, string> = {
      invalid_date_format: 'Formato de data inválido',
      invalid_date_range: 'Período inválido',
      period_overlap: 'Conflito entre itens da planilha',
      existing_stoppage_overlap: 'Conflito com paralisação existente',
      invalid_motive: 'Motivo inválido',
      processing_error: 'Erro de processamento',
      database_error: 'Erro no banco de dados',
      no_valid_data: 'Sem dados válidos',
      validation_error: 'Erro de validação',
      after_docking_end: 'Data posterior ao fim da desatracação',
    };
    return labels[errorType] || errorType;
  };

  return (
    <Modal
      title="Importar Paralisações"
      visible={isOpen}
      onCancel={handleCancel}
      width={validationErrors.length > 0 ? 800 : 520}
      footer={[
        <Button key="back" onClick={handleCancel}>
          Cancelar
        </Button>,
        validationErrors.length > 0 ? (
          <Button key="clear" onClick={() => setValidationErrors([])}>
            Limpar Erros
          </Button>
        ) : null,
        <Button
          key="submit"
          type="primary"
          loading={isLoading}
          onClick={handleUpload}
          disabled={fileList.length === 0}
        >
          Importar
        </Button>,
      ]}
    >
      <p>
        Selecione um arquivo Excel (.xlsx ou .xls) contendo as paralisações a
        serem importadas. O arquivo deve seguir o modelo exibido, com colunas
        para Item, Início, Término, Tempo, Motivo e Observação.
      </p>

      <p>
        <a
          href={`${process.env.PUBLIC_URL}/downloads/Modelo_Paralisacoes.xlsx`}
          download="Modelo_Paralisacoes.xlsx"
        >
          Clique aqui para baixar a planilha modelo
        </a>
      </p>
      <p>
        <strong>Importante:</strong> Os motivos das paralisações devem
        corresponder exatamente aos registrados no sistema (Acidente, Arqueação,
        Chuva e/ou outras condições climáticas desfavoráveis, etc).
      </p>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Upload {...uploadProps}>
          <Button icon={<UploadOutlined />} type="primary">
            Selecionar arquivo Excel
          </Button>
        </Upload>
      </Space>

      {validationErrors.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <Alert
            message="Erros de Validação"
            description={
              <span>
                Foram encontrados <strong>{validationErrors.length}</strong>{' '}
                erro(s) no arquivo.{' '}
                <strong>Nenhuma paralisação foi importada</strong>. Corrija os
                erros abaixo e tente novamente.
              </span>
            }
            type="error"
            icon={<WarningOutlined />}
            showIcon
          />

          <List
            style={{ marginTop: '16px', maxHeight: '300px', overflow: 'auto' }}
            size="small"
            bordered
            dataSource={[...validationErrors].sort((a, b) => {
              const itemA = parseInt(String(a.item), 10) || 0;
              const itemB = parseInt(String(b.item), 10) || 0;
              return itemA - itemB;
            })}
            renderItem={(error) => {
              return (
                <List.Item>
                  <div style={{ width: '100%' }}>
                    <div
                      style={{
                        fontWeight: 'bold',
                        marginBottom: '4px',
                      }}
                    >
                      Item {error.item}: {getErrorTypeLabel(error.error_type)}
                    </div>
                    <div
                      style={{
                        color: '#666',
                        fontSize: '13px',
                      }}
                    >
                      {error.message}
                    </div>
                    {error.overlap_with_item && (
                      <div
                        style={{
                          marginTop: '8px',
                          padding: '8px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '4px',
                          fontSize: '12px',
                        }}
                      >
                        <strong>
                          {error.error_type === 'existing_stoppage_overlap'
                            ? 'Conflito com paralisação existente:'
                            : 'Conflito com item da planilha:'}
                        </strong>{' '}
                        {error.error_type === 'existing_stoppage_overlap'
                          ? `ID: ${error.overlap_with_item}`
                          : `Item ${error.overlap_with_item}`}
                      </div>
                    )}
                  </div>
                </List.Item>
              );
            }}
          />
        </div>
      )}
    </Modal>
  );
}
